import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { Card, Skeleton, Box, useTheme } from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const weekdayOrder = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// ألوان لايت مود
const weekdayColorsLight = [
  "#000000", // أسود
  "#8B4513", // بني
  "#FF0000", // أحمر
  "#4CAF50", // أخضر
  "#1976D2", // أزرق
  "#555555ff", // أبيض
  "#808080", // رمادي
];

// ألوان دارك مود
const weekdayColorsDark = [
  "#FFFFFF", // أبيض
  "#D2B48C", // بني فاتح
  "#FF6B6B", // أحمر فاتح
  "#90EE90", // أخضر فاتح
  "#64B5F6", // أزرق فاتح
  "#BDBDBD", // رمادي فاتح
  "#9E9E9E", // رمادي
];

const AveragePersonsByFloorAndWeekday = () => {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});
  const theme = useTheme();

  const weekdayColors =
    theme.palette.mode === "dark"
      ? weekdayColorsDark
      : weekdayColorsLight;

  const fetchData = async () => {
    try {
      setLoading(true);

      const url = `analytics/floors/people-by-weekday`;
      const { data } = await axios.get(`${baseURL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...filter },
      });

      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching grouped floor data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const floors = [...new Set(dataList.map(item => item.floor_level))];

  const datasets = weekdayOrder.map((weekday, index) => ({
    label: weekday,
    data: floors.map(floor => {
      const match = dataList.find(
        item =>
          item.floor_level === floor &&
          item.weekday_name === weekday
      );
      return match ? match.avg_person_count : 0;
    }),
    backgroundColor: `${weekdayColors[index]}55`,
    borderColor: weekdayColors[index],
    borderWidth: theme.palette.mode === "dark" ? 2 : 1,
  }));

  const chartData = {
    labels: floors,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: true,
        text: "Average Number of Persons by Floor Level and Weekday",
        color: theme.palette.text.primary,
      },
      tooltip: {
        callbacks: {
          label: ctx =>
            `Avg Persons: ${ctx.raw.toFixed(2)} in ${ctx.dataset.label}`,
        },
      },
      datalabels: {
        color: theme.palette.text.primary,
        font: { weight: "bold" },
        formatter: val => {
          if (!val) return "";
          return val.toFixed(2);
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: { color: theme.palette.text.primary },
        title: {
          display: true,
          text: "Average Person Count",
          color: theme.palette.text.primary,
        },
      },
      x: {
        stacked: true,
        ticks: { color: theme.palette.text.primary },
        title: {
          display: true,
          text: "Floor Level",
          color: theme.palette.text.primary,
        },
      },
    },
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
      ) : (
        <Box sx={{ height: 500 }}>
          <Bar data={chartData} options={options} />
        </Box>
      )}
    </Card>
  );
};

export default AveragePersonsByFloorAndWeekday;
